/**
 * Article writing formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const articleWritingFormatsPack: ToilePack = {
    slug: 'article-writing-formats',
    category: 'classroom',
    name: 'Article writing formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'magazine', label: 'Magazine article', subject: 'an article format with "Title", "By-line", "Introduction", "Body", "Conclusion" labelled'},
        {key: 'newspaper', label: 'Newspaper article', subject: 'an article format in two columns with the parts boxed'},
        {key: 'blog', label: 'Blog article', subject: 'an article format drawn as a web page with headline and author line'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Article Writing Format: Magazine, Newspaper, Blog',
        metaDescription: 'Article writing format on one labelled page: a school magazine article, a newspaper article and a blog article, in three styles.',
        h1: 'Article writing format: magazine, newspaper and blog article',
        tagline: 'Nine labelled pages, three articles in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing the layout of an article, with each part labelled: title, by-line, introduction, body and conclusion. The nine pages show an article for a school magazine, a two-column newspaper article and a blog article drawn as a web page with a headline and an author line. Ask for another kind of article and the same labelled layout is asked for.',
        useCases: [
            'a classroom wall chart for article writing',
            'a reference for a journalism club',
            'a handout for a school magazine team',
            'a revision sheet for a writing exam',
        ],
        faq: [
            {q: 'How does the blog page differ?', a: 'It is drawn as a web page, with a headline and an author line above the parts.'},
            {q: 'Does the page include article text?', a: 'No. The body is drawn as ruled lines, so the page shows the layout, not the words.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for an article for another kind of publication?', a: 'Yes. Add a direction for another publication and the labelled layout is asked for with it.'},
        ],
    },
}
